/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable jsx-a11y/heading-has-content */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

import DocumentViewerModal from '../DocumentViewerModal';
import '../../../styles/MarkdownRenderer.css';

// Scoped variant of ReportSummary for the Eligibility Review report only: it
// renders the same markdown as the shared MarkdownRenderer, but turns the
// "Document" column cells into clickable links that open the DocumentViewerModal.
// The shared MarkdownRenderer is intentionally left untouched so other report
// summaries are unaffected.
//
// Clickability is data-driven (the same way the other agents work): a cell is a
// document when its text matches a known filename from the report's `documents`
// list (built in eligibilityNormalizer). That covers every document regardless
// of file extension and avoids false positives.

// Collapses whitespace so markdown cell text and the backend filename match even
// when the report rendering normalizes spacing differently.
const normalizeKey = (text) => text.replace(/\s+/g, ' ').trim();

// Flattens a markdown cell's children down to its plain text content.
const toText = (children) => React.Children.toArray(children)
  .map((child) => {
    if (typeof child === 'string') return child;
    if (child?.props?.children) return toText(child.props.children);
    return '';
  })
  .join('');

const EligibilityReportSummary = ({ summary, documents }) => {
  const [openDoc, setOpenDoc] = useState(null);

  // filename (whitespace-normalized) -> { fileUrl, documentName }
  const documentsByName = useMemo(() => {
    const map = new Map();
    documents.forEach((doc) => {
      if (doc?.fileName && doc?.fileUrl) {
        map.set(normalizeKey(doc.fileName), {
          fileUrl: doc.fileUrl,
          documentName: doc.documentName || doc.fileName,
        });
      }
    });
    return map;
  }, [documents]);

  const components = useMemo(() => ({
    h3: ({ node, ...props }) => <h3 className="md-heading" {...props} />,
    ul: ({ node, ...props }) => <ul className="md-list" {...props} />,
    table: ({ node, ...props }) => (
      <div className="md-table-wrapper">
        <table className="md-table" {...props} />
      </div>
    ),
    th: ({ node, ...props }) => <th className="md-th" {...props} />,
    td: ({ node, children, ...props }) => {
      const match = documentsByName.get(normalizeKey(toText(children)));

      if (match) {
        return (
          <td className="md-td" {...props}>
            <span
              role="button"
              tabIndex={0}
              onClick={() => setOpenDoc(match)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setOpenDoc(match);
              }}
              style={{
                color: '#007bff',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              {children}
            </span>
          </td>
        );
      }

      return <td className="md-td" {...props}>{children}</td>;
    },
  }), [documentsByName]);

  return (
    <>
      <div className="breadcrumb-extra-row">
        <div className="breadcrumb-report">Report Summary:</div>
      </div>

      <div className="breadcrumb-report-box">
        <div className="markdown-wrapper markdown-wrapper--dashboard">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={components}
          >
            {summary}
          </ReactMarkdown>
        </div>
      </div>

      <DocumentViewerModal
        fileUrl={openDoc?.fileUrl}
        documentName={openDoc?.documentName}
        onClose={() => setOpenDoc(null)}
      />
    </>
  );
};

EligibilityReportSummary.propTypes = {
  summary: PropTypes.string.isRequired,
  documents: PropTypes.arrayOf(
    PropTypes.shape({
      fileName: PropTypes.string,
      documentName: PropTypes.string,
      fileUrl: PropTypes.string,
    }),
  ),
};

EligibilityReportSummary.defaultProps = {
  documents: [],
};

export default EligibilityReportSummary;
